/**
 * Cache em memória para dados do Google Sheets
 * Permite compartilhar dados entre componentes sem salvar no banco
 */

type GoogleSheetsCacheEntry = {
  data: any[];
  headers: string[];
  timestamp: number;
};

class GoogleSheetsCache {
  private cache: Map<string, GoogleSheetsCacheEntry> = new Map();
  private listeners: Map<string, Set<(data: any[]) => void>> = new Map();

  /**
   * Gera chave única para o cache baseada no spreadsheet e range
   */
  private getCacheKey(spreadsheetId: string, range: string): string {
    return `${spreadsheetId}:${range}`;
  }

  /**
   * Armazena dados no cache
   */
  set(spreadsheetId: string, range: string, data: any[], headers: string[]): void {
    const key = this.getCacheKey(spreadsheetId, range);
    this.cache.set(key, {
      data,
      headers,
      timestamp: Date.now(),
    });

    // Notifica listeners
    this.notifyListeners(key, data);
  }

  /**
   * Busca dados do cache
   */
  get(spreadsheetId: string, range: string): GoogleSheetsCacheEntry | undefined {
    const key = this.getCacheKey(spreadsheetId, range);
    return this.cache.get(key);
  }

  /**
   * Remove dados do cache
   */
  clear(spreadsheetId?: string, range?: string): void {
    if (spreadsheetId && range) {
      const key = this.getCacheKey(spreadsheetId, range);
      this.cache.delete(key);
      this.listeners.delete(key);
    } else {
      this.cache.clear();
      this.listeners.clear();
    }
  }

  /**
   * Adiciona listener para mudanças nos dados
   */
  subscribe(spreadsheetId: string, range: string, callback: (data: any[]) => void): () => void {
    const key = this.getCacheKey(spreadsheetId, range);
    
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }
    
    this.listeners.get(key)!.add(callback);

    // Retorna função de unsubscribe
    return () => {
      const listenersSet = this.listeners.get(key);
      if (listenersSet) {
        listenersSet.delete(callback);
      }
    };
  }

  /**
   * Notifica todos os listeners sobre mudanças
   */
  private notifyListeners(key: string, data: any[]): void {
    const listenersSet = this.listeners.get(key);
    if (listenersSet) {
      listenersSet.forEach(callback => callback(data));
    }
  }

  /**
   * Verifica se tem dados no cache
   */
  has(spreadsheetId: string, range: string): boolean {
    const key = this.getCacheKey(spreadsheetId, range);
    return this.cache.has(key);
  }
}

// Instância singleton
export const googleSheetsCache = new GoogleSheetsCache();
